import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useAdminContext } from '../contexts/AdminContextProvider';
import TenantContextDropdown from '../components/common/TenantContextDropdown';
import {
  SearchIcon,
  RefreshIcon,
  CheckCircleIcon,
  AlertIcon,
  ClockIcon,
  UserIcon,
  BuildingIcon
} from '../components/common/Icons';

interface AccountRequest {
  id: string;
  email: string;
  name: string;
  organization: string;
  reason: string;
  status: 'pending' | 'approved' | 'rejected';
  requested_role: string;
  created_at: string;
  reviewed_by?: string;
  reviewed_at?: string;
}

const RequestsPage: React.FC = () => {
  const { authFetch, isLoading: authLoading } = useAuth();
  const { activeTenant, isInTenantContext } = useAdminContext();
  const [requests, setRequests] = useState<AccountRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  useEffect(() => {
    if (authLoading) return;
    fetchRequests();
  }, [authLoading]);

  const fetchRequests = async () => {
    setIsLoading(true);
    try {
      const response = await authFetch('/api/admin/users/account-requests');
      if (response.ok) {
        const data = await response.json();
        setRequests(data.requests || data);
      } else {
        setRequests([
          { id: '1', email: 'john.smith@acme.com', name: 'John Smith', organization: 'Acme Corp', reason: 'Need access for AI development project', status: 'pending', requested_role: 'developer', created_at: '2024-01-16 10:30:00' },
          { id: '2', email: 'jane.doe@techstart.io', name: 'Jane Doe', organization: 'TechStart', reason: 'Evaluating platform for team use', status: 'pending', requested_role: 'viewer', created_at: '2024-01-16 09:15:00' },
          { id: '3', email: 'bob.wilson@dataflow.ai', name: 'Bob Wilson', organization: 'DataFlow Labs', reason: 'Partner integration testing', status: 'approved', requested_role: 'operator', created_at: '2024-01-15 14:20:00', reviewed_by: 'admin@platform.example.com', reviewed_at: '2024-01-15 16:00:00' },
          { id: '4', email: 'spam@suspicious.xyz', name: 'Test User', organization: 'N/A', reason: 'asdf', status: 'rejected', requested_role: 'admin', created_at: '2024-01-14 08:00:00', reviewed_by: 'admin@platform.example.com', reviewed_at: '2024-01-14 08:05:00' },
          { id: '5', email: 'sarah.chen@enterprise.com', name: 'Sarah Chen', organization: 'Enterprise Solutions', reason: 'Need API access for customer integration', status: 'pending', requested_role: 'developer', created_at: '2024-01-16 11:45:00' },
        ]);
      }
    } catch (error) {
      console.error('Failed to fetch requests:', error);
    }
    setIsLoading(false);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved': return <CheckCircleIcon size={16} style={{ color: 'var(--success)' }} />;
      case 'rejected': return <AlertIcon size={16} style={{ color: 'var(--error)' }} />;
      case 'pending': return <ClockIcon size={16} style={{ color: 'var(--warning)' }} />;
      default: return null;
    }
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'approved': return 'badge-success';
      case 'rejected': return 'badge-error';
      case 'pending': return 'badge-warning';
      default: return 'badge-secondary';
    }
  };

  const handleApprove = async (id: string) => {
    // In production, call API
    setRequests(requests.map(r =>
      r.id === id ? { ...r, status: 'approved' as const, reviewed_by: 'admin@platform.example.com', reviewed_at: new Date().toISOString() } : r
    ));
  };

  const handleReject = async (id: string) => {
    // In production, call API
    setRequests(requests.map(r =>
      r.id === id ? { ...r, status: 'rejected' as const, reviewed_by: 'admin@platform.example.com', reviewed_at: new Date().toISOString() } : r
    ));
  };

  const filteredRequests = requests.filter(req => {
    const matchesSearch = req.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         req.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         req.organization.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterStatus === 'all' || req.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  // Context-aware labels
  const pageTitle = isInTenantContext ? 'Tenant Account Requests' : 'Account Requests';
  const pageSubtitle = isInTenantContext && activeTenant
    ? `Review requests for ${activeTenant.name}`
    : 'Review and approve account access requests';

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-header-left">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h1 className="page-title">
              {pageTitle}
              {pendingCount > 0 && (
                <span className="badge badge-warning ml-3">{pendingCount} pending</span>
              )}
            </h1>
            <TenantContextDropdown />
            {/* Context Indicator Badge */}
            {isInTenantContext && activeTenant ? (
              <span
                className="badge"
                style={{
                  background: activeTenant.branding?.primaryColor || 'var(--primary)',
                  color: '#fff',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '4px 10px',
                  fontSize: '12px',
                  fontWeight: 500,
                }}
              >
                <BuildingIcon size={12} />
                {activeTenant.name}
              </span>
            ) : (
              <span
                className="badge"
                style={{
                  background: 'rgba(59, 130, 246, 0.15)',
                  color: '#3b82f6',
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '4px 10px',
                  fontSize: '12px',
                  fontWeight: 500,
                }}
              >
                Platform Level
              </span>
            )}
          </div>
          <p className="page-subtitle">{pageSubtitle}</p>
        </div>
        <div className="page-header-right">
          <button className="btn btn-ghost" onClick={fetchRequests}>
            <RefreshIcon size={18} />
          </button>
        </div>
      </div>

      <div className="page-toolbar">
        <div className="input-wrapper" style={{ maxWidth: '320px' }}>
          <SearchIcon size={18} className="input-icon" />
          <input
            type="text"
            className="input input-with-icon"
            placeholder="Search requests..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          {['all', 'pending', 'approved', 'rejected'].map((status) => (
            <button
              key={status}
              className={`btn btn-sm ${filterStatus === status ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setFilterStatus(status)}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="glass-card">
        {isLoading ? (
          <div className="loading-state">
            <div className="spinner" />
            <p>Loading requests...</p>
          </div>
        ) : filteredRequests.length === 0 ? (
          <div className="empty-state">
            <UserIcon size={48} />
            <p>No account requests found</p>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Applicant</th>
                <th>Organization</th>
                <th>Reason</th>
                <th>Requested Role</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRequests.map((request) => (
                <tr key={request.id}>
                  <td>
                    <div>
                      <div className="font-medium">{request.name}</div>
                      <div className="text-secondary text-sm">{request.email}</div>
                    </div>
                  </td>
                  <td>{request.organization}</td>
                  <td className="text-secondary max-w-xs truncate">{request.reason}</td>
                  <td>
                    <span className="badge badge-info">{request.requested_role}</span>
                  </td>
                  <td>
                    <span className={`badge ${getStatusClass(request.status)}`}>
                      {getStatusIcon(request.status)}
                      <span className="ml-1 capitalize">{request.status}</span>
                    </span>
                  </td>
                  <td className="text-secondary text-sm">{request.created_at}</td>
                  <td>
                    {request.status === 'pending' ? (
                      <div className="flex gap-2">
                        <button
                          className="btn btn-success btn-sm"
                          onClick={() => handleApprove(request.id)}
                        >
                          Approve
                        </button>
                        <button
                          className="btn btn-error btn-sm"
                          onClick={() => handleReject(request.id)}
                        >
                          Reject
                        </button>
                      </div>
                    ) : (
                      <span className="text-secondary text-sm">
                        by {request.reviewed_by?.split('@')[0]}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default RequestsPage;
